import { CreateFormControlBar } from "@/components/Common/CreateFormControlBar/CreateFormControlBar";
import type { CreateFormControlBarSummary } from "@/components/Common/CreateFormControlBar/CreateFormControlBar";
import { CreateFormStepsNav } from "@/components/Common/CreateFormStepsNav/CreateFormStepsNav";
import { ClusterFormStep } from "@/components/ClustersPage/VerticalFlowComponents/ClusterFormStep";
import { useSummaryPanelLogic } from "@/components/ClustersPage/VerticalFlowComponents/hooks/useSummaryPanelLogic";
import { usePayAndDeployAction } from "@/components/ClustersPage/VerticalFlowComponents/hooks/usePayAndDeployAction";
import GPUSelector from "@/components/ClustersPage/VerticalFlowComponents/steps/GPUSelector";
import { Loader } from "@/components/Common/Loader/Loader";
import { GlobeNetworkIcon } from "@/components/Icons/GlobeNetworkIcon";
import { LightningBoltIcon } from "@/components/Icons/LightningBoltIcon";
import { LockClosedIcon } from "@/components/Icons/LockClosedIcon";
import { useFormikAutoFieldName } from "@/hooks/useFormikAutoFieldName";
import { useMediaQueryMatch } from "@/hooks/useMediaQueryMatch";
import { useVirtualMachineStore } from "@/store/simpleCluster";
import { ClusterType, DynamicSupplyHardware, SimpleClusterForm } from "@/types";
import { PERSISTED_VM_CLUSTER_KEY } from "@/utils/cluster";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { virtualMachineVerticalFlowAnalytics } from "./analytics";
import { useVirtualMachineFormLogic } from "./hooks/useVirtualMachineFormLogic";
import AddNetworkServices from "./steps/AddNetworkServices";
import ConfigureEnvironmentAndSSHKeys from "./steps/ConfigureEnvironmentAndSSHKeys";
import { InlinePaymentSection } from "./InlinePaymentSection";
import { QuantityDurationSection } from "@/components/ClustersPage/VerticalFlowComponents/QuantityDurationSection";
import { SectionHeader } from "@/components/Common/SectionHeader/SectionHeader";
import { Separator } from "@/components/ui/separator";
import { StandardContainer } from "@/components/Common/StandardContainer/StandardContainer";
import { CreditsPaymentConfirmModal } from "@/components/ClustersPage/CreditsPaymentConfirmModal/CreditsPaymentConfirmModal";
import { SavedConfigsSelect } from "@/components/ClustersPage/SavedDeploymentConfigs/SavedConfigsSelect";
import {
  buildSavePayload,
  canSaveDeploymentConfig
} from "@/components/ClustersPage/SavedDeploymentConfigs/canSaveDeploymentConfig";
import { savedDeploymentPayloadsEqual } from "@/utils/savedDeploymentConfig";
import { SavedDeploymentConfig } from "@/types/savedDeploymentConfig";
import { useToast } from "@/components/ui/use-toast";
import { isPaygCluster } from "@/utils/payg";

export type CreateVirtualMachineFormV2Props = {
  formData: unknown;
  isLoading?: boolean;
  isDisabled?: boolean;
};

export type VirtualMachineFormStepType = "processor" | "environment" | "customize";

const VM_STEPS = [
  { id: "processor", label: "Select VM Processor" },
  { id: "configure", label: "Configure & Deploy" }
];

const VM_STEP_HEADERS = [
  {
    title: "Select Your Virtual Machine Processor",
    subtitle: "Select the processor type optimized for your virtual machine's performance"
  },
  {
    title: "Configure & Deploy",
    subtitle:
      "Set up SSH access, customize your environment, and review payment details before deployment"
  }
];

export const CreateVirtualMachineFormV2: React.FC<CreateVirtualMachineFormV2Props> = ({
  formData
}) => {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const isLg = useMediaQueryMatch("lg");
  const { values } = useFormikContext<SimpleClusterForm>();
  const [selectedSavedConfig, setSelectedSavedConfig] = useState<SavedDeploymentConfig | null>(
    null
  );

  const fetchHardwareWithFilters = useVirtualMachineStore(
    (state) => state.fetchDynamicSupplyHardwareAndFilters
  );
  const fetchClusterHardwareInfo = useVirtualMachineStore(
    (state) => state.fetchClusterHardwareInfo
  );
  const {
    loadingClusterData,
    selectedHardware,
    showSummaryPanel,
    setShowSummaryPanel,
    isPaymentDialogOpen,
    setIsPaymentDialogOpen,
    stepState,
    setHasUserSpecifiedName,
    hasUserSpecifiedName,
    isSubmitDisabled,
    clusterOptions,
    computedValues,
    setCurrentStep,
    onSelectHardware,
    onNextProcessorStep,
    currentStep,
    handleSummaryPanelAction,
    applySavedConfig
  } = useVirtualMachineFormLogic(formData, isLg, fetchHardwareWithFilters);

  useFormikAutoFieldName({ enabled: !hasUserSpecifiedName, items: clusterOptions });

  const handleApplySavedConfig = useCallback(
    async (config: SavedDeploymentConfig) => {
      const { hardwareMatch } = await applySavedConfig(config, {
        buildStepState: (hardwareMatched) => {
          if (hardwareMatched) {
            return {
              processor: "completed",
              environment: "completed",
              customize: "current"
            };
          }
          return {
            processor: "current",
            environment: "next",
            customize: "next"
          };
        }
      });

      if (hardwareMatch.status === "matched") {
        setShowSummaryPanel(true);
      }

      setSelectedSavedConfig(config);

      if (hardwareMatch.status === "missing") {
        toast({
          title: "Hardware unavailable",
          description:
            "Your saved settings were loaded, but the original processor could not be found. Please select a processor.",
          variant: "error"
        });
      } else if (hardwareMatch.status === "sold_out") {
        toast({
          title: "Hardware sold out",
          description:
            "Your saved settings were loaded, but that processor is currently sold out. Please select another.",
          variant: "error"
        });
      } else {
        toast({
          title: "Configuration loaded",
          description: config.name
        });
      }
    },
    [applySavedConfig, setShowSummaryPanel, toast]
  );

  const canSaveConfig = canSaveDeploymentConfig(values, "vm");
  const savePayload = useMemo(
    () => buildSavePayload(values, "vm", selectedHardware),
    [values, selectedHardware]
  );
  const canUpdateLoadedConfig = !selectedSavedConfig
    ? true
    : !savedDeploymentPayloadsEqual(savePayload, selectedSavedConfig.payload, "vm");

  const { totalCostNumber, durationOptions } = useSummaryPanelLogic({
    totalCost: computedValues.totalCostNumber,
    fetchHardwareWithFilters,
    fetchClusterHardwareInfo
  });

  const isOnProcessorStep = currentStep === "processor";
  const isOnConfigureNavStep = stepState.processor === "completed";
  const navStepIndex = isOnConfigureNavStep ? 1 : 0;
  const isPaymentSectionVisible = showSummaryPanel && isOnConfigureNavStep;
  const {
    isPriceLoaded,
    totalCost,
    paymentLink,
    isSubmitting,
    showSavingsMessage,
    totalSavingsAmountFormatted,
    requestPayAndDeploy,
    isCreditsConfirmOpen,
    creditsTopUpPaymentMethod,
    setCreditsTopUpPaymentMethod,
    confirmCreditsPayment,
    cancelCreditsPaymentConfirm,
    cancelPayment,
    mockSpherePayment,
    purchasedCreditsGate,
    minNonPromotionalCreditsRequired
  } = usePayAndDeployAction({
    isActive: isPaymentSectionVisible,
    initialTotalCost: Number(totalCostNumber || 0),
    analytics: virtualMachineVerticalFlowAnalytics,
    persistedStorageKey: PERSISTED_VM_CLUSTER_KEY,
    fetchClusterHardwareInfo
  });

  // Intercept the dialog-open signal from handleSummaryPanelAction and run payment inline instead
  useEffect(() => {
    if (isPaymentDialogOpen) {
      setIsPaymentDialogOpen(false);
      requestPayAndDeploy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentDialogOpen]);

  // Derive hardware summary for the control bar
  const controlBarSummary = useMemo((): CreateFormControlBarSummary | undefined => {
    const hw: DynamicSupplyHardware | null | undefined =
      selectedHardware ??
      (() => {
        if (!values?.hardware) return null;
        const hardwareList =
          ((values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
            ?.hardwareList as DynamicSupplyHardware[]) || [];
        return hardwareList.find((item) => item.hardwareId === values.hardware) ?? null;
      })();

    if (!hw) return undefined;

    const pricePerHr =
      typeof hw.price === "number" && hw.price > 0
        ? `$${hw.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}/hr`
        : "-";

    return {
      processorLabel: hw.hardwareName,
      gpusPerHardware: hw.numCards,
      hourlyPrice: pricePerHr,
      vramPerCard: hw.vramPerCard,
      storage: hw.storage,
      locationCode: hw.location,
      locationName: hw.location
    };
  }, [selectedHardware, values?.hardware, values?.createClusterData]);

  const controlBarBackLabel = isOnProcessorStep ? "Back to VMs" : "Back";
  const controlBarContinueLabel = isOnConfigureNavStep
    ? isSubmitting
      ? "Confirming Payment…"
      : "Pay & Deploy"
    : "Continue";

  const handleControlBarBack = () => {
    if (isOnProcessorStep) {
      navigate("/cloud/virtual-machines");
    } else {
      setShowSummaryPanel(false);
      setCurrentStep("processor");
    }
  };

  const handleControlBarContinue = () => {
    if (isOnProcessorStep) {
      if (!selectedHardware) {
        document
          .getElementById("hardware-grid-container")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      onNextProcessorStep();
      return;
    }
    // Configure & Deploy: validate all fields, surface errors, or start payment.
    void handleSummaryPanelAction();
  };

  const isContinueDisabled = isOnProcessorStep
    ? !selectedHardware
    : isSubmitDisabled || isSubmitting;

  return (
    <form
      className="flex flex-1 flex-col border-t border-border"
      onSubmit={(event) => event.preventDefault()}
    >
      {/* Top step navigation */}
      <CreateFormStepsNav
        steps={VM_STEPS}
        currentStepIndex={navStepIndex}
        title={VM_STEP_HEADERS[navStepIndex].title}
        description={VM_STEP_HEADERS[navStepIndex].subtitle}
        savedInConfig={navStepIndex === 0}
        actions={
          <SavedConfigsSelect
            kind="vm"
            selectedId={selectedSavedConfig?.id}
            selectedConfig={selectedSavedConfig}
            onSelect={handleApplySavedConfig}
            savePayload={savePayload}
            canSave={canSaveConfig}
            canUpdateLoaded={canUpdateLoadedConfig}
            onSaved={setSelectedSavedConfig}
          />
        }
      />

      {/* Content area */}
      <div className="flex flex-col gap-4 pb-4 pt-4">
        {loadingClusterData ? (
          <div className="flex items-center justify-center py-24">
            <Loader color="#ffffff" className="h-[40px] w-[40px]" />
          </div>
        ) : (
          <>
            <ClusterFormStep
              id="processor"
              icon={<LightningBoltIcon className="text-foreground max-lg:h-4 max-lg:w-4" />}
              title=""
              component={
                <StandardContainer>
                  <GPUSelector
                    fetchHardwareWithFilters={fetchHardwareWithFilters}
                    onSelectHardware={(hardware) => {
                      onSelectHardware(hardware);
                      if (!isLg) {
                        setShowSummaryPanel(true);
                      }
                    }}
                    onAdvanceProcessorStep={(hw) => {
                      onSelectHardware(hw);
                      if (!isLg) {
                        setShowSummaryPanel(true);
                      }
                      onNextProcessorStep(hw);
                    }}
                    clusterType={ClusterType.VMAAS}
                    showSummaryPanel={showSummaryPanel}
                    analytics={virtualMachineVerticalFlowAnalytics}
                  />
                </StandardContainer>
              }
              // completedTitle="Chosen GPU"
              // completedComponent={
              //   <GPUStepCompletedCard
              //     processor={computedValues.processorLabel}
              //     numCards={Number(computedValues.gpusPerHardware)}
              //     price={Number(computedValues.priceNumber)}
              //     location={computedValues.locationLabel}
              //     locationId={computedValues.locationId || "-"}
              //   />
              // }
              state={stepState.processor}
              isNextDisabled={!selectedHardware}
              onEdit={() => setCurrentStep("processor")}
            />

            {showSummaryPanel && isOnConfigureNavStep && (
              <>
                <ClusterFormStep
                  id="environment"
                  icon={<LockClosedIcon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                  title=""
                  alwaysExpanded
                  component={
                    <StandardContainer>
                      <ConfigureEnvironmentAndSSHKeys />
                    </StandardContainer>
                  }
                  state={stepState.environment}
                />

                <ClusterFormStep
                  id="customize"
                  icon={<GlobeNetworkIcon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                  title=""
                  component={
                    <>
                      <Separator className="my-3" />
                      <div className="flex flex-col gap-4">
                        <StandardContainer className="flex flex-col gap-6">
                          <SectionHeader
                            title="Customize Your Virtual Machine"
                            subtitle="Change your virtual machine name"
                            savedInConfig
                          />
                          <AddNetworkServices onNameChange={() => setHasUserSpecifiedName(true)} />
                        </StandardContainer>
                        {!isPaygCluster(values.clusterType) && (
                          <>
                            <Separator className="my-3" />
                            <StandardContainer>
                              <QuantityDurationSection
                                durationOptions={durationOptions}
                                className="pt-0"
                              />
                            </StandardContainer>
                          </>
                        )}
                        <InlinePaymentSection
                          isPriceLoaded={isPriceLoaded}
                          totalCost={totalCost}
                          showSavingsMessage={showSavingsMessage}
                          totalSavingsAmountFormatted={totalSavingsAmountFormatted}
                          paymentLink={paymentLink}
                          onMockPayment={
                            paymentLink ? () => mockSpherePayment(paymentLink) : undefined
                          }
                          purchasedCreditsGate={purchasedCreditsGate}
                          minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
                        />
                      </div>
                    </>
                  }
                  completedComponent={<></>}
                  state={stepState.customize}
                  onEdit={() => setCurrentStep("customize")}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom control bar */}
      <CreateFormControlBar
        backLabel={controlBarBackLabel}
        onBack={handleControlBarBack}
        continueLabel={controlBarContinueLabel}
        onContinue={handleControlBarContinue}
        isContinueDisabled={isContinueDisabled}
        isPaymentPending={isSubmitting && isOnConfigureNavStep}
        onConfirmCancelPayment={cancelPayment}
        summary={controlBarSummary}
      />

      <CreditsPaymentConfirmModal
        isOpen={isCreditsConfirmOpen}
        amount={Number(totalCost || totalCostNumber || 0)}
        minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
        isPayg={isPaygCluster(values.clusterType)}
        isLoading={isSubmitting}
        topUpPaymentMethod={creditsTopUpPaymentMethod}
        onTopUpPaymentMethodChange={setCreditsTopUpPaymentMethod}
        onConfirm={confirmCreditsPayment}
        onClose={cancelCreditsPaymentConfirm}
      />
    </form>
  );
};
